/* eslint-disable no-use-before-define */
import extend from 'extend';
import mapBlindItem from './lib/mapBlindItem';
import mapItemInit from './lib/mapItemInit';
import { fetchSimpleReasons } from '../../actions/reasons';
import { retailerSettingsService } from '../retailer-settings/retailer-settings';
import { returnItemsService } from '../return-items-service/return-items-service';
import { store } from '../../store';

const blindReturnSidebarFunction = () => {
  const { blindReturnConfig } = retailerSettingsService.getSettings();
  const returnType = retailerSettingsService.getReturnType();
  const { language } = store.getState();
  const node = document.createElement('div');
  document.body.appendChild(node);
  let updateCallback = null;

  let sidebarState = {
    config: blindReturnConfig,
    open: false,
    loading: true,
    currentItem: {},
    panel: 0,
    requiredFields: [],
    returnReasons: null,
    selectedReason: null,
    temporaryItem: {},
    savedItems: {},
  };

  const updateState = (newState, deep = true) => {
    sidebarState = extend(deep, sidebarState, newState);
    renderSidebar();
  };

  const actions = {
    initialize: (item, index) => updateState(initializeSidebar(item, index), false),

    toggle: () => updateState(toggleSidebar()),

    toggleLoad: () => updateState({ loading: !sidebarState.loading }),

    formChange: (data) => updateState({ currentItem: { ...updateForm(data) } }),

    addInputKey: (inputKey) => updateState({ [inputKey]: null }),

    addRequiredField: (fieldName) =>
      updateState({
        requiredFields: [...sidebarState.requiredFields, fieldName],
      }),

    updateReasons: (returnReasons) => updateState({ returnReasons }),

    selectReason: (reasonIndex) => updateState(selectItemReason(reasonIndex), false),

    resetReason: () => {
      const { currentItem } = sidebarState;
      currentItem.selectedReason = null;
      updateState({ currentItem });
    },

    resetForm: () => updateState({ currentItem: {} }),

    resetSidebar: () => updateState({ open: false }),

    resetSavedItems: () => updateState({ savedItems: {} }, false),

    saveItemProgress: () => updateState({ temporaryItem: saveBlindItemProgress() }, false),

    saveItem: () => updateState(saveBlindItem(), false),

    deleteItem: () => updateState(deleteBlindItem(), false),
  };

  const getReasons = async () => {
    await store.dispatch(fetchSimpleReasons());
    const {
      reasons: { simpleReasonIds, reasonsMap },
    } = store.getState();
    const reasons = simpleReasonIds.map((id) => reasonsMap[id]);
    actions.updateReasons(reasons);
    actions.toggleLoad();
  };

  const selectItemReason = (reasonIndex) => {
    const { currentItem, returnReasons } = sidebarState;
    const chosenReason = returnReasons[reasonIndex];
    const itemUpdate = {
      ...currentItem,
      selectedReason: chosenReason,
      returnDescription: chosenReason.display_name,
      itemStatus: `${language['returns.item.reason_label'] || 'Return Reason:'} ${
        chosenReason.display_name
      }`,
    };

    return { currentItem: itemUpdate };
  };

  const selectSavedReason = (reasonId) => {
    const { returnReasons } = sidebarState;

    const reasonIndex = returnReasons.reduce((accumulator, current, index) => {
      let foundIndex = accumulator;

      if (reasonId === current.return_reason_code_id) {
        foundIndex = index;
      }

      return foundIndex;
    }, null);

    actions.selectReason(reasonIndex);
  };

  const toggleSidebar = () => {
    if (sidebarState.open) {
      actions.saveItemProgress();
    }

    return { open: !sidebarState.open };
  };

  const initializeSidebar = (item = {}) => {
    const { returnReasons, temporaryItem, savedItems } = sidebarState;
    const newItem = Object.keys(item).length === 0;
    const tempItem = temporaryItem[item.itemDescription];
    const savedItem = savedItems[item.itemDescription];
    const unselectedItem = !item.selected;
    let itemInit = {};

    if (newItem) {
      itemInit = { ...temporaryItem };
    } else if (savedItem) {
      itemInit = { ...savedItem };
      selectSavedReason(itemInit.return_reason_code_id);
    } else if (tempItem) {
      itemInit = { ...tempItem };
    } else if (unselectedItem) {
      itemInit = mapItemInit(item);
    }

    if (!returnReasons) {
      getReasons();
    }

    return { currentItem: itemInit };
  };

  const getBlindItems = () => sidebarState.savedItems;

  const saveBlindItemProgress = () => {
    const { currentItem, savedItems, temporaryItem } = sidebarState;
    const currentItemName = currentItem.itemDescription;
    const itemSaved = savedItems[currentItemName] || currentItem.itemReturned;
    const update =
      currentItemName && !itemSaved ? mapBlindItem(currentItem, language) : temporaryItem;
    return update;
  };

  const saveBlindItem = () => {
    const { currentItem, savedItems, temporaryItem } = sidebarState;
    const currentItemName = currentItem.itemDescription;
    const previouslySavedItem = savedItems[currentItemName];
    const newSaveItem = mapBlindItem(currentItem, language);
    const savingTempItem = currentItemName === temporaryItem.itemDescription;

    if (previouslySavedItem) {
      actions.deleteItem();
      returnItemsService.removeItem(previouslySavedItem);
    }

    returnItemsService.addItem(newSaveItem);
    const update = { savedItems: { ...savedItems, [currentItemName]: newSaveItem } };

    if (savingTempItem) {
      update.temporaryItem = {};
    }

    return update;
  };

  const deleteBlindItem = () => {
    const { currentItem, savedItems } = sidebarState;
    const currentItemName = currentItem.itemDescription;
    const update = { currentItem: {}, savedItems: { ...savedItems } };
    returnItemsService.removeItemByName(currentItemName);
    delete update.savedItems[currentItemName];
    return update;
  };

  const setUpdateCallback = (callback) => {
    updateCallback = callback;
  };

  const reasonSelected = (reason, propName) => {
    const { selectedReason } = sidebarState.currentItem;
    return selectedReason && selectedReason[propName] === reason[propName];
  };

  const updateForm = (inputData) => {
    const { name, type } = inputData.target;
    let { value } = inputData.target;
    const trimmedValue = value.trim();

    if (type === 'number') {
      if (value < 1) {
        inputData.target.value = null;
        value = null;
      }

      if (value !== null && value.length > 2) {
        inputData.target.value = sidebarState.currentItem[name];
        value = sidebarState.currentItem[name];
      }
    }

    return { [name]: !trimmedValue ? '' : value };
  };

  const checkRequiredFields = () => {
    const { currentItem, requiredFields } = sidebarState;

    return requiredFields.reduce((accumulator, current) => {
      let invalid = accumulator;

      if (!currentItem[current]) {
        invalid = true;
      }

      return invalid;
    }, false);
  };

  const buildFormState = (formComponent) => {
    const { inputKey, required } = formComponent;
    const { requiredFields } = sidebarState;

    if (inputKey && sidebarState[inputKey] === undefined) {
      actions.addInputKey(inputKey);
    }

    if (required && requiredFields.indexOf(inputKey) < 0) {
      actions.addRequiredField(inputKey);
    }
  };

  const disableNextSections = (requiredValue, component) =>
    requiredValue && !sidebarState[requiredValue] && component !== 'SectionTitle';

  const renderSidebar = async () => {
    const module = await import(
      /* webpackChunkName: "blind-return-sidebar" */ `../../components/BlindReturnSidebar/blindReturnSidebar`
    );
    module.renderBlindSidebar({
      sidebarState,
      actions,
      disableNextSections,
      buildFormState,
      reasonSelected,
      checkRequiredFields,
      returnType,
      updateCallback,
      language,
      node,
    });
  };

  return {
    actions,
    setUpdateCallback,
    getBlindItems,
  };
};

export const blindReturnSidebarService = blindReturnSidebarFunction();
